// src/ConveRegister.tsx

import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { conveAuthApi, conveTokenHelper } from './api/conveApi';
import './ConveRegister.css';

type Step = 'init' | 'code_sent' | 'verified';

interface FieldState {
  value: string;
  error: string;
  success: string;
}

const ConveRegister: React.FC = () => {
  const navigate = useNavigate();

  // 단계
  const [step, setStep] = useState<Step>('init');

  // 필드
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState<FieldState>({ value: '', error: '', success: '' });
  const [code, setCode] = useState<FieldState>({ value: '', error: '', success: '' });
  const [password, setPassword] = useState('');
  const [passwordConfirm, setPasswordConfirm] = useState('');
  const [passwordError, setPasswordError] = useState('');

  // 로딩 상태
  const [loadingCheck, setLoadingCheck] = useState(false);
  const [loadingSend, setLoadingSend] = useState(false);
  const [loadingVerify, setLoadingVerify] = useState(false);
  const [loadingRegister, setLoadingRegister] = useState(false);

  // 타이머
  const [timeLeft, setTimeLeft] = useState(0);
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  //저장된 테마가 없으면 라이트 테마
  useEffect(() => {
    const saved = localStorage.getItem('conve_theme') || 'light';
    document.documentElement.setAttribute('data-theme', saved);
  }, []);

  useEffect(() => {
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, []);

  // 타이머 시작
  const startTimer = () => {
    setTimeLeft(600); // 10분
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      setTimeLeft(prev => {
        if (prev <= 1) {
          clearInterval(timerRef.current!);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const formatTime = (sec: number) => {
    const m = String(Math.floor(sec / 60)).padStart(2, '0');
    const s = String(sec % 60).padStart(2, '0');
    return `${m}:${s}`;
  };

  // 이메일 중복 체크
  const handleCheckEmail = async () => {
    if (!email.value.trim()) {
      setEmail(prev => ({ ...prev, error: '이메일을 입력해주세요.', success: '' }));
      return;
    }
    setLoadingCheck(true);
    try {
      const res = await conveAuthApi.checkEmail(email.value.trim());
      if (res.data.duplicated) {
        setEmail(prev => ({ ...prev, error: '이미 사용 중인 이메일입니다.', success: '' }));
      } else {
        setEmail(prev => ({ ...prev, error: '', success: '사용 가능한 이메일입니다.' }));
      }
    } catch {
      setEmail(prev => ({ ...prev, error: '확인 중 오류가 발생했습니다.', success: '' }));
    } finally {
      setLoadingCheck(false);
    }
  };

  // 인증코드 발송
  const handleSendCode = async () => {
    if (!email.success) {
      setEmail(prev => ({ ...prev, error: '이메일 중복확인을 먼저 해주세요.' }));
      return;
    }
    setLoadingSend(true);
    try {
      await conveAuthApi.sendCode(email.value.trim());
      setStep('code_sent');
      startTimer();
      setCode({ value: '', error: '', success: '' });
    } catch {
      setEmail(prev => ({ ...prev, error: '발송에 실패했습니다. 잠시 후 다시 시도해주세요.' }));
    } finally {
      setLoadingSend(false);
    }
  };

  // 인증코드 확인
  const handleVerifyCode = async () => {
    if (!code.value.trim()) {
      setCode(prev => ({ ...prev, error: '인증코드를 입력해주세요.' }));
      return;
    }
    if (timeLeft === 0) {
      setCode(prev => ({ ...prev, error: '인증코드가 만료되었습니다. 재발송해주세요.' }));
      return;
    }
    setLoadingVerify(true);
    try {
      await conveAuthApi.verifyCode(email.value.trim(), code.value.trim());
      setStep('verified');
      setCode(prev => ({ ...prev, error: '', success: '이메일 인증이 완료되었습니다.' }));
      if (timerRef.current) clearInterval(timerRef.current);
    } catch {
      setCode(prev => ({ ...prev, error: '인증코드가 올바르지 않습니다.' }));
    } finally {
      setLoadingVerify(false);
    }
  };

  // 비밀번호 확인 실시간 체크
  const handlePasswordConfirmChange = (val: string) => {
    setPasswordConfirm(val);
    if (password && val && password !== val) {
      setPasswordError('비밀번호가 일치하지 않습니다.');
    } else {
      setPasswordError('');
    }
  };

  // 회원가입
  const handleRegister = async () => {
    if (!username.trim()) return;
    if (step !== 'verified') return;
    if (!password || password.length < 8) return;
    if (password !== passwordConfirm) return;

    setLoadingRegister(true);
    try {
      const res = await conveAuthApi.register(
        email.value.trim(), password, username.trim()
      );
      const { accessToken, refreshToken, userId, username: uname, email: uEmail } = res.data;
      conveTokenHelper.save(accessToken, refreshToken, { userId, username: uname, email: uEmail });
      navigate('/conve');
    } catch (err: any) {
      alert(err.response?.data?.message || '회원가입에 실패했습니다.');
    } finally {
      setLoadingRegister(false);
    }
  };

  const canRegister =
    username.trim() &&
    step === 'verified' &&
    password.length >= 8 &&
    password === passwordConfirm &&
    !loadingRegister;

  return (
    <div className="conve-auth-wrap">
      <div className="conve-logo">CONV<span>E</span></div>
      <div className="conve-logo-tag">POWERED BY CLAUDE API</div>

      <div className="conve-card">
        <div className="conve-card-title">회원가입</div>

        {/* 이름 */}
        <div className="conve-field">
          <label className="conve-label">이름</label>
          <input
            className="conve-input"
            type="text"
            placeholder="이름을 입력하세요"
            value={username}
            onChange={e => setUsername(e.target.value)}
          />
        </div>

        {/* 이메일 */}
        <div className="conve-field">
          <label className="conve-label">이메일</label>
          <div className="conve-input-row">
            <input
              className={`conve-input ${email.error ? 'error' : email.success ? 'success' : ''}`}
              type="email"
              placeholder="이메일을 입력하세요"
              value={email.value}
              onChange={e => setEmail({ value: e.target.value, error: '', success: '' })}
              disabled={step === 'verified'}
            />
            <button
              className="conve-btn-sm"
              onClick={handleCheckEmail}
              disabled={loadingCheck || step === 'verified'}
            >
              {loadingCheck ? <><span className="conve-spinner-sm" />확인 중</> : '중복확인'}
            </button>
          </div>
          {email.error && <div className="conve-msg error">{email.error}</div>}
          {email.success && !email.error && <div className="conve-msg success">✓ {email.success}</div>}
        </div>

        {/* 인증코드 발송 버튼 */}
        {email.success && step === 'init' && (
          <div className="conve-send-row">
            <button
              className="conve-btn-sm primary"
              onClick={handleSendCode}
              disabled={loadingSend}
            >
              {loadingSend ? <><span className="conve-spinner-sm dark" />발송 중...</> : '인증코드 발송'}
            </button>
          </div>
        )}

        {/* 인증코드 입력 */}
        {(step === 'code_sent' || step === 'verified') && (
          <div className="conve-field">
            <label className="conve-label">
              인증코드
              {step === 'code_sent' && (
                <span className="conve-timer-row">
                  &nbsp;·&nbsp;유효시간&nbsp;
                  <span className={`conve-timer ${timeLeft === 0 ? 'expired' : ''}`}>
                    {formatTime(timeLeft)}
                  </span>
                  {timeLeft === 0 && (
                    <button className="conve-resend" onClick={handleSendCode} disabled={loadingSend}>
                      {loadingSend ? '발송 중...' : ' 재발송'}
                    </button>
                  )}
                </span>
              )}
            </label>
            <div className="conve-input-row">
              <input
                className={`conve-input code ${code.error ? 'error' : code.success ? 'success' : ''}`}
                type="text"
                placeholder="6자리 숫자 입력"
                maxLength={6}
                value={code.value}
                onChange={e => setCode({ value: e.target.value.replace(/\D/g, ''), error: '', success: '' })}
                disabled={step === 'verified'}
              />
              {step === 'code_sent' && (
                <button
                  className="conve-btn-sm"
                  onClick={handleVerifyCode}
                  disabled={loadingVerify}
                >
                  {loadingVerify ? <><span className="conve-spinner-sm" />확인 중</> : '인증확인'}
                </button>
              )}
            </div>
            {code.error && <div className="conve-msg error">{code.error}</div>}
            {code.success && <div className="conve-msg success">✓ {code.success}</div>}
          </div>
        )}

        {/* 비밀번호 */}
        {step === 'verified' && (
          <>
            <div className="conve-field">
              <label className="conve-label">비밀번호 <span className="conve-hint-text">· 8자 이상</span></label>
              <input
                className="conve-input"
                type="password"
                placeholder="비밀번호 입력"
                value={password}
                onChange={e => setPassword(e.target.value)}
              />
            </div>
            <div className="conve-field">
              <label className="conve-label">비밀번호 확인</label>
              <input
                className={`conve-input ${passwordError ? 'error' : (passwordConfirm && !passwordError) ? 'success' : ''}`}
                type="password"
                placeholder="비밀번호 재입력"
                value={passwordConfirm}
                onChange={e => handlePasswordConfirmChange(e.target.value)}
              />
              {passwordError && <div className="conve-msg error">{passwordError}</div>}
              {passwordConfirm && !passwordError && (
                <div className="conve-msg success">✓ 비밀번호가 일치합니다.</div>
              )}
            </div>
          </>
        )}

        {/* 가입 버튼 */}
        <button
          className={`conve-btn-primary ${!canRegister ? 'disabled' : ''}`}
          onClick={handleRegister}
          disabled={!canRegister}
        >
          {loadingRegister
            ? <><span className="conve-spinner-sm dark" />가입 중...</>
            : step !== 'verified'
              ? '가입하기 (인증 완료 후 활성화)'
              : '가입하기'}
        </button>

        <div className="conve-card-footer">
          이미 계정이 있으신가요? <Link to="/conve/login">로그인</Link>
        </div>
      </div>
    </div>
  );
};

export default ConveRegister;
